import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Tooltip,
  useTheme
} from '@material-ui/core'
import {
  CircleCheck,
  Files,
  Glasses,
  Key,
  Lock,
  LockKeyholeOpen,
  Megaphone,
  QrCode,
  RotateCcw,
  RotateCw,
  ScanQrCode,
  Settings,
  Signal,
  SignalLow,
  Trash2,
  TriangleAlert,
  Unlock,
  Unplug,
  Users,
  Wand,
  X
} from 'lucide-react'
import QRCode from 'qrcode.react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import { updateHiddenConnectionAnnouncement } from '../../services/connectionAnnouncementPreferences'
import iconWhatsApp from '../../assets/whatsapp.svg'
import toastError from '../../errors/toastError'
import { copyToClipboard } from '../../helpers/copyToClipboard'
import { generateSecureToken } from '../../helpers/generateSecureToken'
import { i18nToast } from '../../helpers/i18nToast'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { InputField } from '../InputField'
import { InputFieldText } from '../InputFieldText'
import { InputSwitch } from '../InputSwitch'
import SelectInput from '../SelectInput'
import { useStyles } from './style'
import { QueueDepartmentSelect } from '../QueueDepartmentSelect'
import WhatsAppUsersModal from '../WhatsAppUsersModal'

const ConnectionCard = ({
  whatsApp,
  isNew,
  onSave: onSaveProp,
  onCancel: onCancelProp,
  onUpdate,
  onDelete,
  onPasskey,
  onResetPasskey,
  onPrivacy,
  onConnectionAlert,
  onStartSession,
  onRequestNewQrCode,
  onCreateAnnouncement,
  onRefresh,
  onDisconnect,
  autoOpenQrCode
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const { user } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)
  const [name, setName] = useState(whatsApp?.name || '')
  const [nameError, setNameError] = useState(false)
  const [selectedQueueIds, setSelectedQueueIds] = useState(() => {
    if (isNew || !whatsApp?.queues) return []
    return whatsApp.queues.map(q => Number(q.id))
  })
  const [selectedQueueObject, setSelectedQueueObject] = useState(() => {
    if (isNew || !whatsApp?.queues) return null
    return (
      whatsApp.queues.find(q => q.isBot === true) ||
      whatsApp.queues[0] ||
      null
    )
  })
  const [allowAnyDepartment, setAllowAnyDepartment] = useState(() => {
    if (isNew || !whatsApp?.queues) return false
    return whatsApp.queues.some(q => q.isBot === false)
  })
  const [, setIsDirty] = useState(false)
  const [channel, setChannel] = useState(whatsApp?.channel || 'whatsapp')
  const [hubToken, setHubToken] = useState(whatsApp?.hubToken || '')
  const [hubChannel, setHubChannel] = useState(whatsApp?.hubChannel || '')
  const [apiToken, setApiToken] = useState(whatsApp?.token || '')
  const [isApiTokenDisabled, setIsApiTokenDisabled] = useState(true)
  const [showApiToken, setShowApiToken] = useState(false)
  const [botSelectionModalOpen, setBotSelectionModalOpen] = useState(false)
  const [selectedUserIds, setSelectedUserIds] = useState(() => {
    if (isNew || !whatsApp?.users) return []
    return whatsApp.users.map(u => Number(u.id))
  })
  const [usersModalOpen, setUsersModalOpen] = useState(false)
  const [isLocked, setIsLocked] = useState(!isNew)
  const [hasAnnounced, setHasAnnounced] = useState(false)
  const [isAnnouncementDisabled, setIsAnnouncementDisabled] = useState(false)
  const [showQrOverlay, setShowQrOverlay] = useState(false)
  const [overlayQrCode, setOverlayQrCode] = useState('')

  const whatsappId = whatsApp?.id
  const whatsappStatus = whatsApp?.status

  useEffect(() => {
    if (whatsappStatus !== 'qrcode') {
      setShowQrOverlay(false)
    }
  }, [whatsappStatus])

  useEffect(() => {
    if (autoOpenQrCode) {
      setShowQrOverlay(true)
    }
  }, [autoOpenQrCode])

  useEffect(() => {
    if (!showQrOverlay || !whatsappId) return
    let isMounted = true

    const fetchSession = async () => {
      try {
        const { data } = await api.get(`/whatsapp/${whatsappId}`)
        if (isMounted) setOverlayQrCode(data.qrcode || '')
      } catch (err) {
        toastError(err)
      }
    }
    fetchSession()

    return () => {
      isMounted = false
    }
  }, [showQrOverlay, whatsappId])

  useEffect(() => {
    if (!showQrOverlay || !whatsappId) return
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const onCompanyWhatsappSession = data => {
      if (data.action !== 'update' || Number(data.session.id) !== Number(whatsappId)) return

      setOverlayQrCode(data.session.qrcode || '')

      if (data.session.status === 'passkey_required' && onPasskey) {
        setShowQrOverlay(false)
        onPasskey(whatsApp)
        return
      }

      if (data.session.status !== 'qrcode') {
        setShowQrOverlay(false)
      }
    }

    socket.on(`company-${companyId}-whatsappSession`, onCompanyWhatsappSession)

    return () => {
      socket.disconnect()
    }
  }, [showQrOverlay, whatsappId, socketManager, onPasskey, whatsApp])

  useEffect(() => {
    if (whatsApp?.status === 'CONNECTED' || whatsApp?.status === 'qrcode') {
      setHasAnnounced(false)
    }
    if (
      whatsApp?.status === 'DISCONNECTED' &&
      !hasAnnounced &&
      !isNew &&
      whatsApp
    ) {
      onCreateAnnouncement(whatsApp)
      setHasAnnounced(true)
    }
  }, [whatsappStatus, whatsApp, onCreateAnnouncement, hasAnnounced, isNew])


  useEffect(() => {
    if (isNew) {
      setSelectedQueueIds([])
      setAllowAnyDepartment(false)
      setIsDirty(true)
      return
    }

    if (whatsApp) {
      setName(prev => (prev !== whatsApp.name ? whatsApp.name || '' : prev))
      setChannel(prev =>
        prev !== whatsApp.channel ? whatsApp.channel || 'whatsapp' : prev
      )
      setApiToken(prev =>
        prev !== whatsApp.token ? whatsApp.token || '' : prev
      )

      if (whatsApp.extraParameters) {
        setHubToken(whatsApp.extraParameters.hubToken || '')
        setHubChannel(whatsApp.extraParameters.hubChannel || '')
      }

      const backendQueues = whatsApp.queues
      if (Array.isArray(backendQueues) && backendQueues.length > 0) {
        setSelectedQueueIds(backendQueues.map(q => Number(q.id)))
        setSelectedQueueObject(
          backendQueues.find(q => q.isBot === true) || backendQueues[0] || null
        )
        setAllowAnyDepartment(
          backendQueues.some(q => q.isBot === false)
        )
      } else if (Array.isArray(backendQueues)) {
        setSelectedQueueIds([])
        setSelectedQueueObject(null)
        setAllowAnyDepartment(false)
      }
      // quando queues não veio no objeto (atualização parcial via socket),
      // mantém a seleção atual em vez de limpar
    }
  }, [whatsApp, isNew])

  useEffect(() => {
    if (whatsappId) {
      localStorage.setItem(
        `showApiToken-${whatsappId}`,
        JSON.stringify(showApiToken)
      )
    }
  }, [showApiToken, whatsappId])

  useEffect(() => {
    if (!whatsappId) return

    const hiddenIds = user?.hiddenConnectionAnnouncementIds || []
    const disabledFromBackend = hiddenIds.includes(Number(whatsappId))
    const disabledFromStorage =
      localStorage.getItem(
        `doNotShowConnectionAnnouncement_${whatsappId}`
      ) === 'true'

    setIsAnnouncementDisabled(disabledFromBackend || disabledFromStorage)
  }, [whatsappId, user])

  const handleToggleAnnouncement = e => {
    const newValue = e.target.checked
    setIsAnnouncementDisabled(newValue)
    if (whatsappId) {
      // Se o switch for TRUE, ele grava 'true' no storage (não exibe o modal)
      // Se for FALSE, ele remove ou grava 'false' (exibe o modal)
      localStorage.setItem(
        `doNotShowConnectionAnnouncement_${whatsappId}`,
        String(newValue)
      )
      updateHiddenConnectionAnnouncement({
        whatsappId: Number(whatsappId),
        hidden: newValue
      }).catch(error => {
        console.error(
          '[ConnectionCard] Erro ao salvar preferência de alerta:',
          error
        )
      })
    }
  }

  const handleNameChange = useCallback(e => {
    const value = e.target.value.substring(0, 11)
    setName(value)
    if (value.trim().length > 0) {
      setNameError(false)
    }
    setIsDirty(true)
  }, [])

  const handleQueueChange = useCallback(newQueueIds => {
    setSelectedQueueIds(newQueueIds)
    setIsDirty(true)
  }, [])

  const handleQueueSelect = useCallback(selectedItems => {
    const selectedObj = selectedItems.length > 0 ? selectedItems[0] : null
    const ids = selectedObj ? [Number(selectedObj.id)] : []
    setSelectedQueueIds(ids)
    setSelectedQueueObject(selectedObj)
    setIsDirty(true)
    if (selectedObj && selectedObj.isBot === false) {
      setAllowAnyDepartment(true)
    }
  }, [])

  const handleUsersChange = useCallback(ids => {
    setSelectedUserIds(ids)
    setIsDirty(true)
  }, [])

  const handleAllowAnyDepartmentChange = useCallback(e => {
    setAllowAnyDepartment(e.target.checked)
    setIsDirty(true)
  }, [])

  const queueValue = useMemo(() => {
    if (selectedQueueObject) {
      return [selectedQueueObject]
    }
    return Array.isArray(selectedQueueIds) && selectedQueueIds.length > 0
      ? [{ id: Number(selectedQueueIds[0]) }]
      : []
  }, [selectedQueueIds, selectedQueueObject])

  const handleChannelChange = e => {
    setChannel(e.target.value)
    setIsDirty(true)
  }

  const handleHubTokenChange = e => {
    setHubToken(e.target.value)
    setIsDirty(true)
  }

  const handleHubChannelChange = e => {
    setHubChannel(e.target.value)
    setIsDirty(true)
  }

  const handleApiTokenSwitch = () => {
    if (showApiToken) {
      setApiToken('')
      setIsDirty(true)
    }
    setShowApiToken(!showApiToken)
  }

  const handleSave = async () => {
    if (!name || name.trim() === '') {
      setNameError(true)
      toast.error('Preencha o nome da conexão')
      return
    }
    if (selectedQueueIds.length === 0) {
      setBotSelectionModalOpen(true)
      return
    }

    setNameError(false)

    let whatsappData = {
      name: name.trim(),
      queueIds: selectedQueueIds,
      userIds: selectedUserIds,
      isDefault: whatsApp?.isDefault,
      greetingMessage: whatsApp?.greetingMessage,
      complationMessage: whatsApp?.complationMessage,
      outOfHoursMessage: whatsApp?.outOfHoursMessage,
      ratingMessage: whatsApp?.ratingMessage,
      transferMessage: whatsApp?.transferMessage,
      channel: channel,
      provider: whatsApp?.provider,
      token: apiToken
    }

    if (channel === 'notificamehub') {
      whatsappData.extraParameters = { hubToken, hubChannel }
    }

    try {
      if (isNew) {
        await api.post(`/whatsapp`, whatsappData)
        toast.success('Conexão criada com sucesso!')
        if (onSaveProp) onSaveProp()
      } else {
        await api.put(`/whatsapp/${whatsApp.id}`, whatsappData)
        toast.success('Conexão atualizada com sucesso!')
        setIsDirty(false)
        if (onUpdate) onUpdate()
      }
    } catch (err) {
      toastError(err)
    }
  }

  const handleCancel = () => {
    if (isNew) {
      if (onCancelProp) onCancelProp()
      return
    }
    setName(whatsApp.name)
    const whatsQueueIds = whatsApp.queues?.map(queue => Number(queue.id)) || []
    setSelectedQueueIds(whatsQueueIds)
    setSelectedQueueObject(
      whatsApp.queues?.find(queue => queue.isBot === true) ||
        whatsApp.queues?.[0] ||
        null
    )
    setAllowAnyDepartment(
      whatsApp.queues?.some(queue => queue.isBot === false) ?? false
    )
    setSelectedUserIds(whatsApp.users?.map(u => Number(u.id)) || [])
    setChannel(whatsApp.channel)
    if (whatsApp.extraParameters) {
      setHubToken(whatsApp.extraParameters.hubToken)
      setHubChannel(whatsApp.extraParameters.hubChannel)
    }
    setApiToken(whatsApp.token || '')
    setIsApiTokenDisabled(true)
    setIsDirty(false)
  }

  async function generateApiToken() {
    const newToken = generateSecureToken(33)
    setApiToken(newToken)
    setIsApiTokenDisabled(true)
    setIsDirty(true)
  }

  async function deleteApiToken() {
    setApiToken('')
    setIsApiTokenDisabled(false)
    setIsDirty(true)
  }

  async function copyApiToken() {
    copyToClipboard(apiToken)
    i18nToast.success('settings.copiedToClipboard')
  }

  const handleSetDefault = async () => {
    if (isNew || !whatsApp?.id) return
    try {
      const newDefaultValue = !whatsApp.isDefault
      await api.put(`/whatsapp/${whatsApp.id}`, {
        ...whatsApp,
        isDefault: newDefaultValue,
        queueIds: selectedQueueIds
      })
      toast.success(
        newDefaultValue
          ? 'Conexão definida como padrão!'
          : 'Conexão removida do padrão!'
      )
      if (onUpdate) onUpdate()
    } catch (err) {
      toastError(err)
    }
  }

  const renderStatus = () => {
    if (isNew || !whatsApp) return null
    return (
      <div className={classes.customTableCell}>
        {whatsApp?.status === 'DISCONNECTED' && (
          <Tooltip
            title={i18n.t('connections.toolTips.disconnected.title')}
            arrow
          >
            <Signal className={classes.statusDisconnected} size={21} />
          </Tooltip>
        )}
        {whatsApp?.status === 'OPENING' && (
          <CircularProgress size={21} className={classes.buttonProgress} />
        )}
        {whatsApp?.status === 'CONNECTED' && (
          <Tooltip title={i18n.t('connections.toolTips.connected.title')} arrow>
            <Signal className={classes.statusConnected} />
          </Tooltip>
        )}
        {(whatsApp?.status === 'TIMEOUT' || whatsApp.status === 'PAIRING') && (
          <Tooltip title={i18n.t('connections.toolTips.timeout.title')} arrow>
            <SignalLow className={classes.statusTimeout} size={21} />
          </Tooltip>
        )}
        {whatsApp?.status === 'passkey_required' && (
          <Tooltip
            title={
              <>
                <div>{i18n.t('connections.toolTips.passkey.title')}</div>
                <div>{i18n.t('connections.toolTips.passkey.content')}</div>
              </>
            }
            arrow
          >
            <Key className={classes.statusPasskey} size={21} />
          </Tooltip>
        )}
      </div>
    )
  }

  const renderActionButtons = () => {
    if (isNew || !whatsApp) return null
    return (
      <div className={classes.boxStatus}>
        {whatsApp?.status === 'qrcode' && (
          <Button
            className={classes.buttonNewQrCode}
            onClick={() => setShowQrOverlay(true)}
            disabled={isLocked}
          >
            <ScanQrCode />
            <span>Ler QrCode</span>
          </Button>
        )}
        {whatsApp?.status === 'passkey_required' && (
          <>
            <Tooltip title={i18n.t('connections.toolTips.passkey.title')} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onPasskey(whatsApp)}
                  className={classes.statusButton}
                  disabled={isLocked}
                >
                  <Key size={21} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t('connections.toolTips.resetPasskey')} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onResetPasskey(whatsApp.id)}
                  className={classes.statusButton}
                  disabled={isLocked}
                >
                  <RotateCcw size={21} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        {whatsApp?.status === 'DISCONNECTED' && (
          <>
            <Tooltip title={i18n.t('connections.toolTips.retry.content')} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onStartSession(whatsApp.id)}
                  className={classes.statusButton}
                  disabled={isLocked}
                >
                  <RotateCcw size={21} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t('connections.toolTips.newQr.content')} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    onRequestNewQrCode(whatsApp.id)
                    setShowQrOverlay(true)
                  }}
                  className={classes.statusButton}
                  disabled={isLocked}
                >
                  <Wand size={21} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Gerar anúncio" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onCreateAnnouncement(whatsApp)}
                  className={classes.statusButton}
                  disabled={isLocked}
                >
                  <Megaphone size={21} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        {(whatsApp?.status === 'CONNECTED' ||
          whatsApp?.status === 'PAIRING' ||
          whatsApp?.status === 'TIMEOUT') && (
            <Tooltip
              title={i18n.t('connections.toolTips.disconnect.content')}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => onDisconnect('disconnect', whatsApp.id)}
                  className={`${classes.statusButton} ${classes.statusButtonDisconnect}`}
                  disabled={isLocked}
                >
                  <Unplug size={21} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        {whatsApp?.status === 'CONNECTED' &&
          whatsApp.channel === 'whatsapp' && (
            <Tooltip
              title={i18n.t('connections.toolTips.refresh.content')}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => onRefresh(whatsApp)}
                  className={`${classes.statusButton} ${classes.statusWaiting}`}
                  disabled={isLocked}
                >
                  <RotateCw size={21} />
                </IconButton>
              </span>
            </Tooltip>
          )}
      </div>
    )
  }

  return (
    <Paper
      component="article"
      className={`${classes.root} ${showQrOverlay ? classes.rootExpanded : ''}`}
      aria-label={`Conexão ${name || 'Nova'}`}
    >
      <div className={classes.cardContainer}>
        <div className={classes.cardMain}>
          <div className={classes.header}>
            <div className={classes.title}>
              {isNew ? 'Nova Conexão' : whatsApp?.name}
              {!isNew && whatsApp.channel === 'whatsapp' && (
                <img
                  src={iconWhatsApp}
                  alt="WhatsApp"
                  style={{ width: 18, height: 18 }}
                />
              )}
              {!isNew && whatsApp?.id && (
                <Tooltip
                  title={
                    isLocked
                      ? 'Desbloqueie o cadeado para alterar o padrão'
                      : whatsApp.isDefault
                        ? 'Remover padrão'
                        : 'Tornar conexão padrão'
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleSetDefault}
                      disabled={isLocked}
                      className={
                        whatsApp.isDefault
                          ? classes.statusDefault
                          : classes.statusNotDefault
                      }
                    >
                      <CircleCheck
                        size={21}
                        style={{
                          color: whatsApp.isDefault
                            ? theme.palette.green_200.main
                            : theme.palette.type === 'dark'
                              ? theme.palette.dark_50.main
                              : theme.palette.gray_400.main,
                          transition: 'color 0.3s ease'
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {renderStatus()}
              {whatsApp.status === 'CONNECTED' && (
                <>
                  <Tooltip title="Editar Privacidade" arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onPrivacy(whatsApp)}
                        className={classes.buttonActionPrivacy}
                        disabled={isLocked}
                      >
                        <Glasses />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Configurar alerta de desconexão" arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onConnectionAlert(whatsApp)}
                        className={classes.buttonActionPrivacy}
                        disabled={isLocked}
                      >
                        <TriangleAlert />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </div>
            <div className={classes.status}>{renderActionButtons()}</div>
          </div>

          <div className={classes.form}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InputFieldText
                  label="Nome"
                  value={name}
                  onChange={handleNameChange}
                  fullWidth
                  disabled={isLocked}
                  inputProps={{
                    'aria-required': 'true',
                    'aria-invalid': nameError
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                {/* <QueueSelect
                label="Bot"
                selectedQueueIds={selectedQueueIds}
                onChange={ids => {
                  handleQueueChange(ids)
                }}
                isMulti={false}
                filterByIsBot={false}
                disabled={isLocked}
              /> */}
                <QueueDepartmentSelect
                  label="Bot"
                  placeholder="Selecione o Bot"
                  includeAllOption={false}
                  showBots={true}
                  fetchAllQueues={true}
                  filterByIsBot={allowAnyDepartment ? null : false}
                  isMulti={false}
                  disabled={isLocked}
                  value={queueValue}
                  onChange={handleQueueSelect}
                  autoSelectFirst
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} className={classes.switchContainer}>
                <InputSwitch
                  checked={allowAnyDepartment}
                  onChange={handleAllowAnyDepartmentChange}
                  label="Permitir selecionar qualquer departamento"
                  disabled={isLocked}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <ButtonPrimary
                  label="Usuários / Departamentos"
                  icon={Users}
                  onClick={() => setUsersModalOpen(true)}
                  disabled={isLocked}
                  fullWidth
                  style={{ height: 'auto', minHeight: 25, padding: '4px 8px', lineHeight: 1.2 }}
                />
              </Grid>
              <Grid item xs={6}>
                <span>{selectedUserIds.length} usuário(s) vinculado(s)</span>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <SelectInput
                  label={'Tipo de Conexão'}
                  value={channel}
                  onChange={handleChannelChange}
                  disabled={isLocked}
                >
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="notificamehub">Api</MenuItem>
                </SelectInput>
              </Grid>
              <Grid item xs={6} className={classes.switchContainer}>
                <InputSwitch
                  checked={showApiToken}
                  onChange={handleApiTokenSwitch}
                  label="Exibir token"
                  disabled={isLocked}
                />
              </Grid>

              <Grid item xs={12} className={classes.switchContainer}>
                <InputSwitch
                  checked={isAnnouncementDisabled}
                  onChange={handleToggleAnnouncement}
                  label="Silenciar alertas de desconexão"
                  disabled={isLocked}
                />
              </Grid>
            </Grid>

            {channel === 'notificamehub' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InputField
                    label="Api Account Token"
                    fullWidth
                    value={hubToken}
                    onChange={handleHubTokenChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputField
                    label="Api Channel Token"
                    fullWidth
                    value={hubChannel}
                    onChange={handleHubChannelChange}
                  />
                </Grid>
              </Grid>
            )}

            {showApiToken && (
              <InputFieldText
                label="API Token"
                value={apiToken}
                disabled={isApiTokenDisabled}
                InputProps={{
                  endAdornment: (
                    <>
                      {apiToken && (
                        <>
                          <Tooltip title="Copiar Token" arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={copyApiToken}
                                disabled={isLocked}
                              >
                                <Files size={20} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {!isApiTokenDisabled && (
                            <Tooltip title="Excluir Token" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={deleteApiToken}
                                  disabled={isLocked}
                                >
                                  <Trash2 size={20} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={
                              isApiTokenDisabled
                                ? 'Desbloquear Token'
                                : 'Bloquear Token'
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setIsApiTokenDisabled(!isApiTokenDisabled)
                                }
                                disabled={isLocked}
                              >
                                {isApiTokenDisabled ? (
                                  <Lock size={20} />
                                ) : (
                                  <Unlock size={20} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {!apiToken && (
                        <Tooltip title="Gerar API Token" arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={generateApiToken}
                              disabled={isLocked}
                            >
                              <Settings size={20} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </>
                  )
                }}
              />
            )}

            <div className={classes.actions}>
              {!isNew && (
                <Tooltip
                  title={isLocked ? 'Desbloquear para editar' : 'Bloquear edição'}
                >
                  <IconButton
                    size="small"
                    onClick={() => setIsLocked(!isLocked)}
                    className={classes.iconButtonLocked}
                  >
                    {isLocked ? (
                      <Lock size={20} />
                    ) : (
                      <LockKeyholeOpen size={20} />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              {!isLocked && (
                <>
                  {!isNew && (
                    <Tooltip title="Excluir Conexão" arrow>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onDelete('delete', whatsApp.id)}
                          className={classes.buttonActionDelete}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <div
                    className={classes.buttonsAction}
                    style={{ marginLeft: 'auto' }}
                  >
                    <ButtonSecondary
                      onClick={() => {
                        handleCancel()
                        if (!isNew) setIsLocked(true)
                      }}
                      label="Cancelar"
                    />
                    <ButtonPrimary onClick={handleSave} label="Salvar" />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {showQrOverlay && (
          <div className={classes.qrSidePanel}>
            <Tooltip title="Fechar QR Code" arrow>
              <IconButton
                size="small"
                onClick={() => setShowQrOverlay(false)}
                className={classes.qrOverlayCloseButton}
              >
                <X size={20} />
              </IconButton>
            </Tooltip>
            {overlayQrCode ? (
              <QRCode
                className={classes.qrOverlayFrame}
                value={overlayQrCode}
                size={320}
              />
            ) : (
              <div className={classes.qrOverlayWaiting}>
                <QrCode size={160} />
                <span>Aguardando gerar o QR Code</span>
              </div>
            )}
            <span className={classes.qrOverlayInfo}>
              Para concluir a conexão, abra o WhatsApp no seu dispositivo móvel e escaneie o QR Code exibido aqui. Isso permitirá a sincronização e o início do atendimento integrado no sistema.
            </span>
          </div>
        )}
      </div>

      <WhatsAppUsersModal
        open={usersModalOpen}
        onClose={() => setUsersModalOpen(false)}
        selectedUserIds={selectedUserIds}
        onChange={handleUsersChange}
        whatsAppQueues={whatsApp?.queues || []}
      />

      <Dialog
        open={botSelectionModalOpen}
        onClose={() => setBotSelectionModalOpen(false)}
        maxWidth="xs"
        aria-labelledby="bot-dialog-title"
        aria-describedby="bot-dialog-description"
      >
        <DialogTitle id="bot-dialog-title" className={classes.dialogTitle}>
          Seleção de Bot Obrigatória
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText id="bot-dialog-description">
            Para salvar esta conexão, é necessário vincular um bot primeiro.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ButtonPrimary
            onClick={() => setBotSelectionModalOpen(false)}
            label="Ok"
          />
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default ConnectionCard
